import type { AnalysisContext, BatchDetections, Candidate, CandidateOverview, CandidatePosition, CandidateReview, CandidateSignalList, CandidateSignalOutcome, CandidateSourceEnvironment, CandidateSplit, CandidateSourceMerge, CandidateStatus, DetectionOptions, DetectionPreview, FeedbackOnlyList, GeneralizationHistory, GeneralizationSource, JsonObject, ManualPromotionBody, ManualPromotionResult, RereviewResult, RescanResult, RetryResult, ReviewStageState, RunPurpose, SignalStages } from "@/types/issuePipeline";
import type {
  CatalogEnvironment,
  CatalogModel,
  CatalogObservations,
  DeploymentRecord,
  DeploymentRequest,
  EnableResult,
  ModelCreateInput,
  ModelPolicy,
  ModelVersion,
  ModelVersionInput,
  OpenRouterLookup,
  PricingInput,
  PricingList,
  PricingRow,
  ReconcileRequest,
  RegistrationInput,
  RegistrationResult,
  Requirements,
  SettleRequest,
  TargetDiagnosis,
  TestCallAnswer,
  TestCallPreview,
  TestCallTarget,
} from "@/types/modelCatalog";
import type {
  AdminPromptState,
  AdminSessionMessagesResponse,
  AgentFile,
  AgentFileContent,
  AgentState,
  BatchCostOverview,
  BatchPageDetail,
  BatchPageListResponse,
  BatchPageMode,
  BatchResultDetail,
  BatchRubric,
  BatchRunView,
  ConversationSessionListResponse,
  DelegatedAIComposeAcceptedResponse,
  DelegatedAIMessageResponse,
  EngagementEnvironment,
  EvalCounts,
  FeedbackCategory,
  HomeFeedListResponse,
  HomeFeedLogDetail,
  HomeFeedSummaryResponse,
  InviteSuggestionsResponse,
  NegativeFeedbackListResponse,
  NegativeFeedbackItem,
  NegativeFeedbackReplayRunListResponse,
  PagelessRunListResponse,
  ReplayCommitListResponse,
  ProactiveLogDetail,
  ProactiveLogListResponse,
  ProactiveSessionSearchResponse,
  ProactiveUserSearchResponse,
  PromotionResult,
  PromptPreview,
  PromptVersion,
  PromptVersionCreate,
  PromptVersionList,
  LlmModelOption,
  MatrixPage,
  SharedSessionsByEmailResponse,
  SubAgentProfileVersion,
  SubAgentProfileVersionList,
  SkillCatalog,
  TeBaseline,
  TeSpec,
  TestCaseDetail,
  CriteriaRegenerationBody,
  CriteriaRegenerationResult,
  CriteriaVersionList,
  TestCaseListResponse,
  TestCasePatchBody,
  TestExecutionListResponse,
  TriggerResolution,
  ReferenceJob,
  ReferenceOptions,
  ReferenceRequest,
  ReferenceList,
  ReferenceReviewRequest,
  ReferenceReviewResult,
  ReferenceReviewStatus,
  ReferenceVersionList,
  CauseLoopTargetList,
  ReferenceScoreResult,
  TcLoopProgress,
} from "@/types";
import type {
  LlmTraceResponse,
  PromptBindingName,
  PromptBindingVersion,
} from "@/types/llmTrace";
import type { CallSiteDetail, CallSiteEnvironment, CallSiteSaveResult, CallSiteValidation, CallSitesView, LogicalCatalogModel, PromptCallSitesView } from "@/types/callSites";
import type { LlmCallFilters, LlmCallList, LlmIoDetail, LlmIoEnvironment, LlmIoStorage } from "@/types/llmIo";

type QueryValue = string | number | boolean | undefined;

type RequestOptions = {
  method?: string;
  body?: unknown;
  /**
   * An array becomes a repeated parameter (`?cats=a&cats=b`), not a joined
   * string — that is how the eval routes take multi-value filters (BE §19.2).
   */
  query?: Record<string, QueryValue | readonly QueryValue[]>;
  formData?: FormData;
  timeoutMs?: number;
};

/**
 * Carries the admin API's error code alongside the human message.
 *
 * The admin routes answer with `{"detail": {"error": {"code", "message"}}}`.
 * Stringifying that whole object put the code in front of the user as raw JSON
 * while still leaving callers nothing to branch on. Extending Error keeps every
 * existing `err instanceof Error ? err.message : ...` site working unchanged.
 */
export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
    readonly code = "",
    /** The structured error as sent, for fields beyond code/message (e.g. 409's `job_id`). */
    readonly detail: Record<string, unknown> = {},
  ) {
    super(message);
    this.name = "ApiError";
  }
}

/** `GET /api/v1/admin/sams/admins/me`: the caller's own SAMS access. */
export type SamsAccess = { user_id: string; email: string; admin: boolean; prod_access: boolean };

/**
 * One row of `GET /api/v1/admin/sams/admins` (replay_lab/sams_admins.py
 * `AdminDirectory.list`): the `sams_admin` columns plus the SAMS auth facts of
 * its user. `email_verified` is null when no SAMS auth user has this id.
 */
export type SamsAdmin = {
  user_id: string;
  email: string;
  role: string;
  can_execute_prod: boolean;
  added_by: string;
  added_at: string;
  revoked_at: string | null;
  revoked_by: string | null;
  providers: string[];
  email_verified: boolean | null;
};

/**
 * What revoke and prod-execution answer: the `sams_admin` row alone
 * (sams_admins.py `_row`, :420 / :457) — only grant adds the auth facts.
 */
export type SamsAdminRow = Omit<SamsAdmin, "providers" | "email_verified">;

/**
 * One row of `GET /api/v1/admin/sams/admins/users` (contract fixed by root
 * 2026-10-14): every SAMS auth sign-up, admin or not, admins first, then by
 * most recent sign-in; no search, `limit` only (default 200). `admin` is null
 * for an account that never was one; a revoked admin has `active: false`.
 */
export type SamsUser = {
  user_id: string;
  email: string;
  providers: string[];
  email_verified: boolean;
  created_at: string;
  last_sign_in_at: string | null;
  admin: null | {
    active: boolean;
    can_execute_prod: boolean;
    added_by: string;
    added_at: string;
    revoked_at: string | null;
  };
};

/** The admin-directory actions; every other audit action is `<area>.<verb>` (e.g. `tc.update`). */
export type SamsAdminAction = "seed" | "grant" | "revoke" | "grant_prod" | "revoke_prod";

/**
 * One row of `GET /api/v1/admin/sams/admins/events`, the SAMS audit log.
 * `user_id`/`email`/`role` are set only on admin-directory events (the five
 * `SamsAdminAction`s); other events name what they touched by
 * `target_type`/`target_id`. `actor_kind` is the server's effective value: an
 * admin event without `actor_user_id` is `seed`; a system job's `actor_email`
 * is `system:<name>`. `masked`: the caller has no prod access, so the server
 * hid this prod event's `target_id`, `summary` and `detail`.
 */
export type SamsAdminEvent = {
  event_id: string;
  action: SamsAdminAction | (string & {});
  user_id: string | null;
  email: string | null;
  role: string | null;
  actor_user_id: string | null;
  actor_email: string;
  created_at: string;
  actor_kind: "user" | "system" | "seed";
  operation_id: string | null;
  phase: "intent" | "result" | "reconcile" | null;
  target_type: string | null;
  target_id: string | null;
  environment: "dev" | "prod" | null;
  outcome: "ok" | "failed" | "rejected" | "unknown" | "pending" | null;
  summary: string | null;
  detail: Record<string, unknown> | null;
  masked: boolean;
  /** What was done, as a Korean sentence end ("가격을 추가했습니다"); null for an action without words. */
  action_label?: string | null;
  /** The target in words; null when unknown or masked (it never names what the mask withholds). */
  target_label?: string | null;
};

/** Filters and paging of the audit log; `cursor` is the opaque `next_cursor` of the previous page. */
export type SamsAuditEventQuery = {
  limit?: number;
  cursor?: string | null;
  /** One prefix, or several (any of them matches): the model catalog's history reads two. */
  action_prefix?: string | string[];
  target_type?: string;
  target_id?: string;
  environment?: "dev" | "prod";
  operation_id?: string;
};

/** One page of the audit log, newest first (created_at, then event_id, descending). */
export type SamsAuditEventPage = { items: SamsAdminEvent[]; next_cursor: string | null };

// --- SAMS prompt deployments (replay_lab/sams_prompt_pipeline_routes.py) ---

export type PromptDeployEnvironment = "dev" | "prod";

/** What one environment serves for a row: the catalog version SAMS recorded, and Base's row version. */
export type PromptDeploymentServed = {
  catalog_version: number | null;
  applied_version: number | null;
  outcome: string;
  recorded_at: string;
  actor: string;
};

/** A live Base row that differs from what SAMS recorded (`target_drift`). */
export type PromptDeploymentDrift = {
  subject: "prompt" | "profile";
  key: string;
  locale: string;
  role: string | null;
  recorded_sha256: string | null;
  live_sha256: string | null;
};

/** One row of `GET …/prompt-deployments` (`PromptPipeline.status`). */
export type PromptDeploymentRow = {
  subject: "prompt" | "profile";
  key: string;
  locale: string;
  role: string | null;
  catalog: { version: number } | null;
  dev: PromptDeploymentServed | null;
  prod: PromptDeploymentServed | null;
  dev_pending: boolean;
  prod_pending: boolean;
  catalog_inactive: boolean;
  /** Per environment: null = verified, no drift; "unverified" = live Base not read. */
  drift: Record<PromptDeployEnvironment, PromptDeploymentDrift | null | "unverified">;
};

export type PromptDeploymentStatus = {
  rows: PromptDeploymentRow[];
  dev_pending: number;
  prod_pending: number;
  prod_verification: "verified" | "forbidden" | "not_requested";
};

/** `POST …/{environment}` with `dry_run: true` (`PromptPipeline._summary`). */
export type PromptDeploymentPreview = {
  target_environment: PromptDeployEnvironment;
  preview_sha256: string;
  changed: { subject: "prompt" | "profile"; key: string; locale: string; role: string | null;
             catalog_version: number | null; withdraw: boolean }[];
  unchanged: number;
  settled_withdrawals: string[];
  drift: PromptDeploymentDrift[];
  drift_sha256: string | null;
};

/** The confirmed `POST …/{environment}` (`PromptPipeline._record`). */
export type PromptDeploymentResult = {
  deployment_id: string;
  target_environment: PromptDeployEnvironment;
  state: "applied" | "unchanged";
  outcomes: string[];
  records_written?: number;
  recorded: boolean;
};

const PROMPT_DEPLOYMENTS = "/api/v1/admin/sams/prompt-deployments";

/** Base targets of the composer's sends; SAMS relays to prod only. */
export type ProactiveSendEnvironment = "prod";
const PROACTIVE_SENDS = "/api/v1/admin/sams/proactive-messages";

/** The SAMS model catalog: SAMS-only for every method, with no Base fallback. */
const MODEL_CATALOG = "/api/v1/admin/sams/models";

export type ErrorEnvironment = "dev" | "prod" | "sams";
export interface BackendError {
  error_log_id: string;
  environment: ErrorEnvironment;
  turn_id: string | null;
  tool_call_id: string | null;
  event: string;
  tool_name: string;
  provider: string | null;
  error_kind: string;
  http_status: number | null;
  message: string;
  duration_ms: number | null;
  occurred_at: string;
  test_case_id?: string | null;
  origin_environment?: ErrorEnvironment;
  origin_error_log_id?: string | null;
  origin_turn_id?: string | null;
  origin_tool_call_id?: string | null;
  request_payload?: Record<string, unknown>;
  error_body?: string | null;
  error_body_truncated?: boolean;
}
export interface BackendErrorPage {
  items: BackendError[];
  next_cursor: string | null;
  unavailable_sources: { environment: ErrorEnvironment; code: string }[];
}

export class AdminApi {
  /** Set by `forSamsLogin`: the bearer is the SAMS login token. */
  private samsLogin = false;
  private accessRequest?: Promise<SamsAccess>;

  constructor(
    private readonly baseUrl: string,
    private readonly bearerToken: string,
    private readonly replayLabBaseUrl: string = baseUrl,
    private readonly samsBaseUrl?: string,
    private readonly evaluationRuntime: boolean = false,
  ) {}

  /**
   * The app's client: it holds the SAMS login token, which only the SAMS host
   * accepts. Without a SAMS address (`VITE_SAMS_API_BASE_URL`) every request
   * fails as a configuration error instead of sending that token to Base.
   */
  static forSamsLogin(baseUrl: string, samsToken: string, replayLabBaseUrl: string, samsBaseUrl?: string) {
    const api = new AdminApi(baseUrl, samsToken, replayLabBaseUrl, samsBaseUrl);
    api.samsLogin = true;
    return api;
  }

  /** Explicit owner context; source dev/prod never chooses this runtime DB. */
  forEvaluationRuntime() {
    const api = new AdminApi(this.baseUrl, this.bearerToken, this.replayLabBaseUrl,
      this.samsBaseUrl, true);
    api.samsLogin = this.samsLogin;
    return api;
  }

  /**
   * The sources this login may read. SAMS answers prod reads only for an admin
   * with prod access (403 PROD_ACCESS_FORBIDDEN otherwise), so the screens
   * that read both ask SAMS once (`/admin/sams/admins/me`) and request dev
   * only without it. A client that is not a SAMS login reads both.
   */
  async readableEnvironments(): Promise<("dev" | "prod")[]> {
    if (!this.samsLogin) return ["dev", "prod"];
    return (await this.samsAccess()).prod_access === true ? ["dev", "prod"] : ["dev"];
  }

  listBackendErrors(environment: ErrorEnvironment | "all" = "all", cursor?: string | null) {
    return this.request<BackendErrorPage>("/api/v1/admin/sams/backend-errors", {
      query: { environment, cursor: cursor || undefined, limit: 50 },
    });
  }

  getBackendError(environment: ErrorEnvironment, id: string) {
    return this.request<BackendError>(`/api/v1/admin/sams/backend-errors/${environment}/${encodeURIComponent(id)}`);
  }

  /**
   * The caller's own SAMS access (`/admin/sams/admins/me`), asked once per
   * client; a failure is not kept, so the next call asks again. SAMS answers it
   * for an active admin only: anyone else gets its 403.
   */
  /** Drop the cached `/me` answer so the next `samsAccess` asks SAMS again (after an admin change). */
  forgetSamsAccess() {
    this.accessRequest = undefined;
  }

  async samsAccess(): Promise<SamsAccess> {
    this.accessRequest ??= this.request<SamsAccess>("/api/v1/admin/sams/admins/me");
    try {
      return await this.accessRequest;
    } catch (err) {
      this.accessRequest = undefined;
      throw err;
    }
  }

  // --- SAMS admins (replay_lab/sams_admin_routes.py) ---
  // Every one of these is an admin-only SAMS route; the server applies the
  // rules (LAST_ADMIN, prod holders, no self prod change) and the UI shows
  // its refusal as it came.

  async listSamsAdmins(): Promise<SamsAdmin[]> {
    return (await this.request<{ items: SamsAdmin[] }>("/api/v1/admin/sams/admins")).items ?? [];
  }

  /** The first page of the audit log (`limit` 1..500): its items only. */
  async listSamsAdminEvents(limit = 100): Promise<SamsAdminEvent[]> {
    return (await this.listSamsAuditEvents({ limit })).items;
  }

  /**
   * One page of the audit log. Pass the previous page's `next_cursor` as
   * `cursor` for the next one; the cursor is opaque — never build one here.
   */
  async listSamsAuditEvents(params: SamsAuditEventQuery = {}): Promise<SamsAuditEventPage> {
    const { limit = 100, cursor, action_prefix, target_type, target_id, environment, operation_id } = params;
    const page = await this.request<Partial<SamsAuditEventPage>>("/api/v1/admin/sams/admins/events", {
      query: { limit, cursor: cursor || undefined,
               action_prefix: (Array.isArray(action_prefix) ? action_prefix.length > 0 : action_prefix) ? action_prefix : undefined,
               target_type: target_type || undefined, target_id: target_id || undefined,
               environment, operation_id: operation_id || undefined },
    });
    return { items: page.items ?? [], next_cursor: page.next_cursor ?? null };
  }

  /** Every SAMS sign-up with its admin state (`limit` omitted: the server's 200). */
  async listSamsUsers(limit?: number): Promise<SamsUser[]> {
    return (await this.request<{ items: SamsUser[] }>("/api/v1/admin/sams/admins/users",
      { query: { limit } })).items ?? [];
  }

  /** Add (or reactivate) the SAMS auth user `user_id`; its verified email must equal `email`. */
  grantSamsAdmin(body: { user_id: string; email: string }) {
    return this.request<SamsAdmin>("/api/v1/admin/sams/admins", { method: "POST", body });
  }

  revokeSamsAdmin(userId: string) {
    return this.request<SamsAdminRow>(`/api/v1/admin/sams/admins/${encodeURIComponent(userId)}/revoke`,
      { method: "POST" });
  }

  setSamsAdminProdExecution(userId: string, allowed: boolean) {
    return this.request<SamsAdminRow>(
      `/api/v1/admin/sams/admins/${encodeURIComponent(userId)}/prod-execution`,
      { method: "POST", body: { allowed } },
    );
  }

  /** Catalog / dev / prod per prompt and sub-agent role; `verify` reads those live Base targets for drift. */
  promptDeploymentStatus(verify: PromptDeployEnvironment[] = []) {
    return this.request<PromptDeploymentStatus>(PROMPT_DEPLOYMENTS, { query: { verify } });
  }

  /** What deploying every catalog prompt to `environment` would change (no side effect). */
  previewPromptDeployment(environment: PromptDeployEnvironment) {
    return this.request<PromptDeploymentPreview>(`${PROMPT_DEPLOYMENTS}/${environment}`,
      { method: "POST", body: { dry_run: true } });
  }

  /** Apply the confirmed preview; `acceptDrift` is the preview's drift_sha256 when the dialog showed drift. */
  deployPrompts(environment: PromptDeployEnvironment, previewSha256: string, acceptDrift?: string | null) {
    return this.request<PromptDeploymentResult>(`${PROMPT_DEPLOYMENTS}/${environment}`, {
      method: "POST",
      body: { preview_sha256: previewSha256, ...(acceptDrift ? { accept_drift: acceptDrift } : {}) },
      timeoutMs: 180000,
    });
  }

  get hasSamsRuntimeHost() { return Boolean(this.samsBaseUrl?.trim()); }

  /**
   * False when this client holds a SAMS token: Base does not accept it, so
   * Base-only commands are unavailable rather than sent and rejected.
   */
  get canCallBaseDirectly() {
    if (!this.hasSamsRuntimeHost) return !this.samsLogin;
    return trimSlash(this.baseUrl) === trimSlash(this.samsBaseUrl!);
  }

  absoluteUrl(path?: string | null) {
    if (!path) return "";
    if (path.startsWith("http://") || path.startsWith("https://")) return path;
    // SAMS points agent_avatar_url here only when it serves the avatar; without a
    // SAMS address the path still resolves against Base, as before.
    if (path.startsWith(SAMS_PREPARED_PROFILE_PREFIX) && this.hasSamsRuntimeHost) {
      return `${this.samsBaseUrl!.replace(/\/+$/, "")}${path}`;
    }
    return `${this.normalizedBaseUrl}${path}`;
  }

  async listAgents(): Promise<AgentState[]> {
    const json = await this.request<{ agents: AgentState[] }>(
      "/api/v1/admin/synapse-agents",
    );
    return json.agents || [];
  }

  async skillCatalog(): Promise<SkillCatalog> {
    return this.request<SkillCatalog>(
      "/api/v1/admin/synapse-agents/skill-catalog",
    );
  }

  async createAgent(body: Record<string, unknown>): Promise<AgentState> {
    return this.request<AgentState>("/api/v1/admin/synapse-agents", {
      method: "POST",
      body,
    });
  }

  async patchAgent(identifier: string, body: Record<string, unknown>) {
    return this.request<AgentState>(
      `/api/v1/admin/synapse-agents/${encodeURIComponent(identifier)}`,
      { method: "PATCH", body },
    );
  }

  async patchCatalog(identifier: string, body: Record<string, unknown>) {
    return this.patchAgentComponent(identifier, "catalog", body);
  }

  async patchPrompt(identifier: string, body: Record<string, unknown>) {
    return this.patchAgentComponent(identifier, "prompt", body);
  }

  async patchSkills(identifier: string, body: Record<string, unknown>) {
    return this.patchAgentComponent(identifier, "skills", body);
  }

  async patchAvatar(identifier: string, body: Record<string, unknown>) {
    return this.patchAgentComponent(identifier, "avatar", body);
  }

  async uploadAvatar(identifier: string, file: File) {
    const formData = new FormData();
    formData.append("image", file, file.name);
    return this.request<AgentState>(
      `/api/v1/admin/synapse-agents/${encodeURIComponent(identifier)}/avatar/image`,
      { method: "POST", formData },
    );
  }

  async promoteToProd(identifier: string) {
    return this.request<PromotionResult>(
      `/api/v1/admin/synapse-agents/${encodeURIComponent(identifier)}/promote-to-prod`,
      {
        method: "POST",
        body: { confirmation: "PROMOTE_TO_PROD" },
        timeoutMs: 300000,
      },
    );
  }

  async listFiles(identifier: string, prefix = "") {
    const json = await this.request<{ files: AgentFile[] }>(
      `/api/v1/admin/synapse-agents/${encodeURIComponent(identifier)}/files`,
      { query: { prefix } },
    );
    return json.files || [];
  }

  async readFile(identifier: string, path: string) {
    return this.request<AgentFileContent>(
      `/api/v1/admin/synapse-agents/${encodeURIComponent(identifier)}/files/content`,
      { query: { path } },
    );
  }

  async deleteFile(identifier: string, path: string) {
    await this.request<unknown>(
      `/api/v1/admin/synapse-agents/${encodeURIComponent(identifier)}/files`,
      { method: "DELETE", query: { path } },
    );
  }

  async uploadFile(
    identifier: string,
    path: string,
    file: File,
    ingest: boolean,
  ) {
    const formData = new FormData();
    formData.append("path", path);
    formData.append("ingest", String(ingest));
    formData.append("file", file, file.name);
    return this.request<unknown>(
      `/api/v1/admin/synapse-agents/${encodeURIComponent(identifier)}/files`,
      { method: "POST", formData },
    );
  }

  async listNegativeFeedbackQA({
    limit = 30,
    unresolvedOnly = false,
    compact = true,
    cursor,
    environment = "dev",
  }: {
    limit?: number;
    unresolvedOnly?: boolean;
    compact?: boolean;
    cursor?: string | null;
    environment?: "dev" | "prod" | "all";
  } = {}): Promise<NegativeFeedbackListResponse> {
    return this.request<NegativeFeedbackListResponse>(
      "/api/v1/admin/negative-feedback",
      {
        query: {
          limit,
          unresolved_only: unresolvedOnly,
          compact,
          cursor: cursor || undefined,
          environment,
          include_prod: environment === "all",
        },
      },
    );
  }

  async getNegativeFeedbackQADetail(environment: string, feedbackId: string) {
    return this.request<NegativeFeedbackItem>(
      `/api/v1/admin/negative-feedback/${encodeURIComponent(environment)}/${encodeURIComponent(feedbackId)}`,
      { timeoutMs: 120000 },
    );
  }

  /**
   * Full LLM execution trace behind one assistant answer.
   *
   * Admin API, not the Replay Lab. `environment` picks the database the same
   * way the negative-feedback routes do: `dev` is the API's own database and
   * `prod` is read through the admin prod pool, so a prod case traces from the
   * dev admin API instead of 404ing as a missing message.
   *
   * Ephemeral Replay runs use the stored snapshot. Retained evaluation runs
   * use an explicit SAMS owner context; no missing-record fallback is made.
   */
  async getLlmTrace(
    sessionId: string,
    messageId: string,
    environment: string = "dev",
  ) {
    return this.request<LlmTraceResponse>(
      `/api/v1/admin/sessions/${encodeURIComponent(sessionId)}/messages/${encodeURIComponent(messageId)}/llm-trace`,
      { query: { environment }, timeoutMs: 120000 },
    );
  }

  /** Read-only call catalog; kept independent from the existing trace API. */
  async listLlmCalls({
    environment = "dev", storage = "turn", source, status, sessionId, turnId,
    since, until, cursor, limit = 50,
  }: LlmCallFilters = {}): Promise<LlmCallList> {
    return this.request<LlmCallList>("/api/v1/admin/llm-calls", {
      query: { environment, storage, source, status, session_id: sessionId,
        turn_id: turnId, since, until, cursor: cursor ?? undefined, limit },
    });
  }

  async getLlmCallIo(
    recordId: string,
    { environment = "dev", storage = "turn", callId }: {
      environment?: LlmIoEnvironment; storage?: LlmIoStorage; callId?: string | null;
    } = {},
  ): Promise<LlmIoDetail> {
    return this.request<LlmIoDetail>(
      `/api/v1/admin/llm-calls/${encodeURIComponent(recordId)}/io`,
      { query: { environment, storage, call_id: callId ?? undefined }, timeoutMs: 120000 },
    );
  }

  /** The raw IO of one sub-agent round — rows saved before rounds were
   *  recorded separately answer 404 (they live on the parent turn's IO). */
  async getSubAgentRoundIo(
    executionId: string,
    roundIndex: number,
    { environment = "dev" }: { environment?: LlmIoEnvironment } = {},
  ): Promise<LlmIoDetail> {
    return this.request<LlmIoDetail>(
      `/api/v1/admin/sub-agent-executions/${encodeURIComponent(executionId)}/rounds/${roundIndex}/llm-io`,
      { query: { environment }, timeoutMs: 120000 },
    );
  }

  /**
   * One session's message history — the conversation llm-trace hangs off.
   *
   * Newest-first on the server, returned oldest-first; pass the previous
   * response's `next_before_seq` as `beforeSeq` to walk further back.
   * `environment` picks the database like `getLlmTrace` does, so one admin
   * client reads dev and prod sessions without re-authenticating.
   */
  async listSessionMessages(
    sessionId: string,
    {
      limit = 100,
      beforeSeq,
      includeDeleted = false,
      environment = "dev",
    }: {
      limit?: number;
      beforeSeq?: number | null;
      includeDeleted?: boolean;
      environment?: string;
    } = {},
  ) {
    return this.request<AdminSessionMessagesResponse>(
      `/api/v1/admin/sessions/${encodeURIComponent(sessionId)}/messages`,
      {
        query: {
          environment,
          limit,
          before_seq: beforeSeq ?? undefined,
          // Only send the flag when it is on: `request` stringifies whatever it
          // gets, and `include_deleted=false` would read as truthy to FastAPI.
          include_deleted: includeDeleted ? true : undefined,
        },
      },
    );
  }

  async listReplayCommits(
    environment: string,
    feedbackId: string,
    date: string,
  ) {
    return this.request<ReplayCommitListResponse>(
      `/api/v1/commits/${encodeURIComponent(environment)}/${encodeURIComponent(feedbackId)}`,
      {
        query: { date },
        timeoutMs: 120000,
      },
      this.replayLabBaseUrl,
    );
  }

  /**
   * Saved replay runs, newest first.
   *
   * Keyset-paginated: pass back the previous response's `next_cursor` to get
   * the next page. The cursor is opaque — never build one client-side.
   */
  async listNegativeFeedbackReplayRuns(
    environment: string,
    feedbackId: string,
    limit = 20,
    cursor?: string | null,
  ) {
    return this.request<NegativeFeedbackReplayRunListResponse>(
      `/api/v1/admin/negative-feedback/${encodeURIComponent(environment)}/${encodeURIComponent(feedbackId)}/replay-runs`,
      {
        query: { limit, cursor: cursor || undefined },
        timeoutMs: 120000,
      },
    );
  }

  async allowSecretHost(body: {
    owner_kind?: "user" | "group";
    owner_email?: string;
    owner_id?: string;
    name: string;
    host: string;
  }) {
    return this.request<unknown>("/api/v1/admin/secrets/allow-host", {
      method: "POST",
      body,
    });
  }

  async promptState() {
    return this.request<AdminPromptState>("/api/v1/admin/prompts");
  }

  /**
   * One prompt row by the id a turn's `prompt_bindings` recorded.
   *
   * Editing a prompt only deactivates the old row, so the version a past answer
   * ran on is still readable — which is the point: a trace says "22k tokens on
   * base_prompt v14" and this is what shows v14 rather than whatever is active
   * now. The body is the source template; see PromptBindingVersion.
   */
  async getPromptVersion(name: PromptBindingName, promptId: string) {
    return this.request<PromptBindingVersion>(
      `/api/v1/admin/prompts/bindings/${encodeURIComponent(name)}/${encodeURIComponent(promptId)}`,
    );
  }

  async patchAdminPrompt(target: string, body: Record<string, unknown>) {
    return this.request<AdminPromptState>(
      `/api/v1/admin/prompts/${encodeURIComponent(target)}`,
      { method: "PATCH", body },
    );
  }

  async patchSubAgentProfile(role: string, body: Record<string, unknown>) {
    return this.request<AdminPromptState>(
      `/api/v1/admin/prompts/sub-agents/${encodeURIComponent(role)}`,
      { method: "PATCH", body },
    );
  }

  // --- Prompt versions: save does not change what serves; activate does. ---

  async listPromptVersions(scope: PromptScope) {
    return this.request<PromptVersionList>(promptScopePath(scope), {
      query: promptScopeQuery(scope),
    });
  }

  async savePromptVersion(scope: PromptScope, body: PromptVersionCreate) {
    return this.request<PromptVersion>(promptScopePath(scope), {
      method: "POST",
      body,
      query: promptScopeQuery(scope),
    });
  }

  async activatePromptVersion(scope: PromptScope, versionId: string) {
    return this.request<PromptVersionList>(
      `${promptScopePath(scope)}/${encodeURIComponent(versionId)}/activate`,
      { method: "POST", query: promptScopeQuery(scope) },
    );
  }

  async deletePromptVersion(scope: PromptScope, versionId: string) {
    return this.request<PromptVersionList>(
      `${promptScopePath(scope)}/${encodeURIComponent(versionId)}`,
      { method: "DELETE", query: promptScopeQuery(scope) },
    );
  }

  async resummarizePromptVersion(scope: PromptScope, versionId: string) {
    return this.request<PromptVersion>(
      `${promptScopePath(scope)}/${encodeURIComponent(versionId)}/summary`,
      { method: "POST", query: promptScopeQuery(scope) },
    );
  }

  /** Render unsaved template text into the system prompt(s) it is part of. */
  async previewPromptDraft(target: string, content: Record<string, string | null>) {
    return this.request<{ previews: PromptPreview[] }>(
      `/api/v1/admin/prompts/${encodeURIComponent(target)}/preview`,
      { method: "POST", body: { content } },
    );
  }

  async listSubAgentProfileVersions(role: string) {
    return this.request<SubAgentProfileVersionList>(
      `/api/v1/admin/prompts/sub-agents/${encodeURIComponent(role)}/versions`,
    );
  }

  async saveSubAgentProfileVersion(role: string, body: Record<string, unknown>) {
    return this.request<SubAgentProfileVersion>(
      `/api/v1/admin/prompts/sub-agents/${encodeURIComponent(role)}/versions`,
      { method: "POST", body },
    );
  }

  async activateSubAgentProfileVersion(role: string, profileId: string) {
    return this.request<SubAgentProfileVersionList>(
      `/api/v1/admin/prompts/sub-agents/${encodeURIComponent(role)}/versions/${encodeURIComponent(profileId)}/activate`,
      { method: "POST" },
    );
  }

  async deleteSubAgentProfileVersion(role: string, profileId: string) {
    return this.request<SubAgentProfileVersionList>(
      `/api/v1/admin/prompts/sub-agents/${encodeURIComponent(role)}/versions/${encodeURIComponent(profileId)}`,
      { method: "DELETE" },
    );
  }

  async listLlmModels() {
    return this.request<{ models: LlmModelOption[] }>("/api/v1/admin/llm-models");
  }

  // --- SAMS model catalog (MODELCAT6-S5-api-contract-proposal.md) ---
  // SAMS is the catalog's one owner: every method goes to SAMS and none falls
  // back to Base (`request` refuses without a SAMS address).

  async listCatalogModels(): Promise<CatalogModel[]> {
    return (await this.request<{ items: CatalogModel[] }>(MODEL_CATALOG)).items ?? [];
  }

  async listModelVersions(modelId: string): Promise<ModelVersion[]> {
    return (await this.request<{ items: ModelVersion[] }>(
      `${MODEL_CATALOG}/${encodeURIComponent(modelId)}/versions`)).items ?? [];
  }

  async createCatalogModel(body: ModelCreateInput) {
    return this.request<CatalogModel>(MODEL_CATALOG, { method: "POST", body });
  }

  async saveModelVersion(modelId: string, body: ModelVersionInput, baseVersion: number) {
    return this.request<CatalogModel>(`${MODEL_CATALOG}/${encodeURIComponent(modelId)}/versions`,
      { method: "POST", body: { ...body, base_version: baseVersion } });
  }

  /** CATUX U2: model, capabilities and first price in one SAMS transaction (the model starts off and hidden). */
  async registerCatalogModel(body: RegistrationInput) {
    return this.request<RegistrationResult>(`${MODEL_CATALOG}/registrations`, { method: "POST", body });
  }

  /** CATUX U2: a new enabled version of the model (its capabilities linked again) and the general check. */
  async enableCatalogModel(modelId: string, baseVersion: number, changeSummary?: string) {
    return this.request<EnableResult>(`${MODEL_CATALOG}/${encodeURIComponent(modelId)}/enable`,
      { method: "POST", body: { base_version: baseVersion, ...(changeSummary ? { change_summary: changeSummary } : {}) } });
  }

  async lookupOpenRouterModel(name: string) {
    return this.request<OpenRouterLookup>(`${MODEL_CATALOG}/lookup/openrouter`, { method: "POST", body: { name } });
  }

  async listCatalogPricing(filter: { provider?: string; model?: string } = {}): Promise<PricingList> {
    const page = await this.request<Partial<PricingList>>(`${MODEL_CATALOG}/pricing`,
      { query: { provider: filter.provider || undefined, model: filter.model || undefined } });
    return { items: page.items ?? [], now: page.now ?? new Date().toISOString() };
  }

  async createCatalogPricing(body: PricingInput) {
    return this.request<PricingRow>(`${MODEL_CATALOG}/pricing`, { method: "POST", body });
  }

  async retractCatalogPricing(catalogPricingId: string) {
    return this.request<PricingRow>(`${MODEL_CATALOG}/pricing/${encodeURIComponent(catalogPricingId)}/retract`,
      { method: "POST" });
  }

  async modelRequirements(environment: CatalogEnvironment) {
    return this.request<Requirements>(`${MODEL_CATALOG}/requirements`, { query: { environment } });
  }

  async listModelDeployments(filter: { model_id?: string; catalog_pricing_id?: string; limit?: number } = {}) {
    return (await this.request<{ items: DeploymentRecord[] }>(`${MODEL_CATALOG}/deployments`, {
      query: { model_id: filter.model_id || undefined, catalog_pricing_id: filter.catalog_pricing_id || undefined,
               limit: filter.limit },
    })).items ?? [];
  }

  async deployCatalogItem(body: DeploymentRequest) {
    return this.request<DeploymentRecord>(`${MODEL_CATALOG}/deployments`, { method: "POST", body, timeoutMs: 180000 });
  }

  async reconcileCatalogItem(body: ReconcileRequest) {
    return this.request<DeploymentRecord>(`${MODEL_CATALOG}/deployments/reconcile`,
      { method: "POST", body, timeoutMs: 180000 });
  }

  /** D1: accept the Base row a needs_operator delivery left (its sha256); answers the operator_settled record. */
  async settleCatalogItem(body: SettleRequest) {
    return this.request<DeploymentRecord>(`${MODEL_CATALOG}/deployments/settle`,
      { method: "POST", body, timeoutMs: 180000 });
  }

  async modelPolicy() {
    return this.request<ModelPolicy>(`${MODEL_CATALOG}/policy`);
  }

  /** [지금 확인] (CATUX U1): Base's rows against the catalog, read only; nothing is recorded. */
  async observeCatalog(environment: CatalogEnvironment) {
    return this.request<CatalogObservations>(`${MODEL_CATALOG}/observations`, { query: { environment }, timeoutMs: 120000 });
  }

  /** Each target's connection, schema, privileges and policy visibility as codes (CATUX U1). */
  async listModelTargets() {
    return (await this.request<{ items: TargetDiagnosis[] }>(`${MODEL_CATALOG}/targets`, { timeoutMs: 120000 })).items ?? [];
  }

  // --- model connection test (CATUX U3) ---
  // A preview calls nothing; a run sends one request (no retry). The run may take as long as the site's own
  // timeout, so its client timeout is longer than the default.

  async previewTestCall(target: TestCallTarget) {
    return this.request<TestCallPreview>(`${MODEL_CATALOG}/test-calls/preview`, { method: "POST", body: target });
  }

  async runTestCall(target: TestCallTarget, message: string) {
    return this.request<TestCallAnswer>(`${MODEL_CATALOG}/test-calls`,
      { method: "POST", body: { ...target, message }, timeoutMs: 240000 });
  }

  // --- call-site assignments (MODELALL U5/U14) ---
  // The site list and the prompt screen read the same rows; prod is read only with prod access (server-side).

  async listCallSiteRows(prompt?: string) {
    return this.request<CallSitesView>(`${MODEL_CATALOG}/call-sites/view`, { query: { prompt: prompt || undefined } });
  }

  /** The sites that read one prompt (a prompt key or the editor's target name). */
  async promptCallSites(prompt: string) {
    return this.request<PromptCallSitesView>(`${MODEL_CATALOG}/call-sites/by-prompt/${encodeURIComponent(prompt)}`);
  }

  /** One site in one environment: its active version, history and start value (site_view). */
  async callSite(environment: CallSiteEnvironment, key: string) {
    return this.request<CallSiteDetail>(`${MODEL_CATALOG}/call-sites/${encodeURIComponent(key)}`,
      { query: { environment } });
  }

  async listLogicalCatalog() {
    return (await this.request<{ items: LogicalCatalogModel[] }>(`${MODEL_CATALOG}/catalog`)).items ?? [];
  }

  async validateCallSite(environment: CallSiteEnvironment, key: string, content: Record<string, unknown>) {
    return this.request<CallSiteValidation>(`${MODEL_CATALOG}/call-sites/validate`,
      { method: "POST", body: { environment, changes: { [key]: content } } });
  }

  async saveCallSiteVersion(key: string, body: {
    environment: CallSiteEnvironment; content: Record<string, unknown>; base_version: number | null;
    change_summary?: string; confirm_incomparable?: boolean; reason?: string;
  }) {
    return this.request<CallSiteSaveResult>(`${MODEL_CATALOG}/call-sites/${encodeURIComponent(key)}/versions`,
      { method: "POST", body });
  }

  async listConversations({
    q,
    limit = 50,
    statusFilter = "active",
  }: {
    q?: string;
    limit?: number;
    statusFilter?: "active" | "inactive" | "all";
  } = {}) {
    return this.request<ConversationSessionListResponse>("/api/v1/conversations", {
      query: {
        q: q || undefined,
        limit,
        status_filter: statusFilter,
      },
    });
  }

  async sharedSessionsByEmail(email: string) {
    return this.request<SharedSessionsByEmailResponse>("/api/v1/users/shared-sessions", {
      query: { email },
    });
  }

  async inviteSuggestions(limit = 50) {
    return this.request<InviteSuggestionsResponse>("/api/v1/users/me/invite-suggestions", {
      query: { limit },
    });
  }

  /** SAMS only: the product users of one source (Base's search reads just its own DB). */
  async searchSourceUsers(environment: "dev" | "prod", q = "", limit = 50) {
    return this.request<ProactiveUserSearchResponse>(
      "/api/v1/admin/sams/source/users",
      { query: { environment, q: q || undefined, limit } },
    );
  }

  async searchProactiveUsers(q = "", limit = 50) {
    return this.request<ProactiveUserSearchResponse>(
      "/api/v1/admin/proactive-messages/users",
      { query: { q: q || undefined, limit } },
    );
  }

  async searchProactiveSessions({
    q = "",
    targetUserEmail = "",
    limit = 50,
    environment = "dev",
    runPurpose,
  }: {
    q?: string;
    targetUserEmail?: string;
    limit?: number;
    runPurpose?: RunPurpose | "all";
    /** `prod` searches the prod DB through the admin prod pool. */
    environment?: string;
  } = {}) {
    return this.request<ProactiveSessionSearchResponse>(
      "/api/v1/admin/proactive-messages/sessions",
      {
        query: {
          environment,
          q: q || undefined,
          target_user_email: targetUserEmail || undefined,
          run_purpose: runPurpose,
          limit,
        },
      },
    );
  }

  // The composer's sends go to SAMS (replay_lab/sams_proactive_proxy.py), which
  // relays them to that environment's Base with its own service account and
  // returns Base's answer unchanged. The body is Base's contract. There is no
  // Base-direct path: without the service account SAMS answers 503
  // SAMS_PROACTIVE_BASE_CREDENTIAL_MISSING.
  async postDelegatedAIMessage(
    environment: ProactiveSendEnvironment,
    sessionId: string,
    body: {
      target_user_email: string;
      message: string;
      agent_version?: number;
    },
  ) {
    return this.request<DelegatedAIMessageResponse>(
      `${PROACTIVE_SENDS}/${environment}/sessions/${encodeURIComponent(sessionId)}/delegated-ai-message`,
      { method: "POST", body, timeoutMs: 120000 },
    );
  }

  async composeDelegatedAIMessage(
    environment: ProactiveSendEnvironment,
    sessionId: string,
    body: {
      target_user_email: string;
      instruction: string;
      agent_version?: number;
      model?: string;
      llm_model?: string | null;
      reasoning_effort?: "low" | "medium" | "high" | null;
    },
  ) {
    return this.request<DelegatedAIComposeAcceptedResponse>(
      `${PROACTIVE_SENDS}/${environment}/sessions/${encodeURIComponent(sessionId)}/delegated-ai-compose`,
      { method: "POST", body, timeoutMs: 120000 },
    );
  }

  // --- Engagement console -------------------------------------------------
  // Read-only. Each call targets one environment; the pages fan out to dev
  // and prod in parallel the way NegativeReviews does.

  async listProactiveLog({
    environment,
    cursor,
    limit = 30,
    kind = "all",
    email,
    sessionId,
    sinceDays = 14,
  }: {
    environment: EngagementEnvironment;
    cursor?: string | null;
    limit?: number;
    kind?: "all" | "auto" | "manual";
    email?: string;
    sessionId?: string;
    sinceDays?: number;
  }) {
    return this.request<ProactiveLogListResponse>(
      "/api/v1/admin/proactive-messages/log",
      {
        query: {
          environment,
          cursor: cursor || undefined,
          limit,
          kind,
          email: email || undefined,
          session_id: sessionId || undefined,
          since_days: sinceDays,
        },
      },
    );
  }

  async getProactiveLogDetail(
    environment: EngagementEnvironment,
    messageId: string,
  ) {
    return this.request<ProactiveLogDetail>(
      `/api/v1/admin/proactive-messages/log/${encodeURIComponent(environment)}/${encodeURIComponent(messageId)}`,
      { timeoutMs: 120000 },
    );
  }

  async listHomeFeedItems({
    environment,
    cursor,
    limit = 30,
    type = "all",
    state,
    email,
    sinceDays = 14,
  }: {
    environment: EngagementEnvironment;
    cursor?: string | null;
    limit?: number;
    type?: "all" | "suggestion" | "reply_reminder";
    state?: string;
    email?: string;
    sinceDays?: number;
  }) {
    return this.request<HomeFeedListResponse>("/api/v1/admin/home-feed/items", {
      query: {
        environment,
        cursor: cursor || undefined,
        limit,
        type,
        state: state || undefined,
        email: email || undefined,
        since_days: sinceDays,
      },
    });
  }

  async getHomeFeedItemDetail(
    environment: EngagementEnvironment,
    itemId: string,
  ) {
    return this.request<HomeFeedLogDetail>(
      `/api/v1/admin/home-feed/items/${encodeURIComponent(environment)}/${encodeURIComponent(itemId)}`,
      { timeoutMs: 120000 },
    );
  }

  async homeFeedSummary({
    environment,
    sinceDays = 7,
    type = "all",
  }: {
    environment: EngagementEnvironment;
    sinceDays?: number;
    type?: "all" | "suggestion" | "reply_reminder";
  }) {
    return this.request<HomeFeedSummaryResponse>(
      "/api/v1/admin/home-feed/summary",
      { query: { environment, since_days: sinceDays, type } },
    );
  }

  // --- Evaluation / Test Cases --------------------------------------------
  // Contract: base_agent_spec/sams/TECH_SPEC_BE.md §19.
  //
  // No `environment` parameter anywhere here, unlike the negative-feedback and
  // engagement calls. v1 reads the dev database only: prod has no
  // `eval_test_case` table, and a row there could not be retired, scored or
  // given a reference anyway (§19.6). `source_environment` is a column on the
  // row, not a switch for which database to ask.

  async listTestCases({
    cursor,
    limit = 30,
    activeOnly = true,
    environment,
    sourceEnvironment,
    categories,
    uncategorizedOnly = false,
    triggerResolution,
    q,
  }: {
    cursor?: string | null;
    limit?: number;
    activeOnly?: boolean;
    /** Which database to read — BE reads both by default and merges them. */
    environment?: "all" | "dev" | "prod";
    sourceEnvironment?: string;
    /** Repeated on the wire, OR-matched against the snapshot column. */
    categories?: FeedbackCategory[];
    uncategorizedOnly?: boolean;
    triggerResolution?: TriggerResolution;
    q?: string;
  } = {}) {
    return this.request<TestCaseListResponse>("/api/v1/admin/eval/test-cases", {
      query: {
        cursor: cursor || undefined,
        limit,
        active_only: activeOnly,
        environment: environment || undefined,
        source_environment: sourceEnvironment || undefined,
        categories: categories?.length ? categories : undefined,
        uncategorized_only: uncategorizedOnly || undefined,
        trigger_resolution: triggerResolution || undefined,
        q: q || undefined,
      },
    });
  }

  async getTestCase(testCaseId: string) {
    return this.request<TestCaseDetail>(
      `/api/v1/admin/eval/test-cases/${encodeURIComponent(testCaseId)}`,
      { timeoutMs: 120000 },
    );
  }

  /** `title` and `is_active` only — everything else about a TC is immutable. */
  async patchTestCase(testCaseId: string, body: TestCasePatchBody) {
    return this.request<TestCaseDetail>(
      `/api/v1/admin/eval/test-cases/${encodeURIComponent(testCaseId)}`,
      { method: "PATCH", body },
    );
  }

  /**
   * Make new criteria from every 👎 on the answer (TCFEEDBACK B8). Only on a
   * person's click. `environment` is the TC's `source_environment`; the old
   * criteria and old scores stay as they are. Long: one model call.
   */
  async regenerateTestCaseCriteria(
    testCaseId: string,
    environment: string,
    body: CriteriaRegenerationBody,
  ) {
    return this.request<CriteriaRegenerationResult>(
      `/api/v1/admin/eval/test-cases/${encodeURIComponent(testCaseId)}/criteria-regenerations`,
      { method: "POST", body, query: { environment }, timeoutMs: 180000 },
    );
  }

  /** Every criteria version of one TC (TCFEEDBACK B8). */
  async listTestCaseCriteriaVersions(testCaseId: string, environment: string) {
    return this.request<CriteriaVersionList>(
      `/api/v1/admin/eval/test-cases/${encodeURIComponent(testCaseId)}/criteria-versions`,
      { query: { environment } },
    );
  }

  async listTestExecutions(
    testCaseId: string,
    { cursor, limit = 20 }: { cursor?: string | null; limit?: number } = {},
  ) {
    return this.request<TestExecutionListResponse>(
      `/api/v1/admin/eval/test-cases/${encodeURIComponent(testCaseId)}/executions`,
      { query: { cursor: cursor || undefined, limit } },
    );
  }

  // --- Batch Test -----------------------------------------------------------
  // Contract: base_agent_spec/sams/fe/TEST_CASES_BATCH.md · BE admin_eval_batch.

  /** What "as deployed" means now — the TE editor's baseline and model list. */
  async getTeBaseline() {
    return this.request<TeBaseline>("/api/v1/admin/eval/te-baseline");
  }

  /** Newest first. `testCaseId` keeps the pages whose set holds that TC. */
  async listBatchPages({
    cursor,
    limit = 30,
    testCaseId,
  }: { cursor?: string | null; limit?: number; testCaseId?: string } = {}) {
    return this.request<BatchPageListResponse>("/api/v1/admin/eval/batch-pages", {
      query: { cursor: cursor || undefined, limit, test_case_id: testCaseId || undefined },
    });
  }

  /** A new, unlocked page. `test_case_ids` is only a starting draft. */
  async createBatchPage(body: { mode: BatchPageMode; name?: string; test_case_ids?: string[] }) {
    return this.request<BatchPageDetail>("/api/v1/admin/eval/batch-pages", {
      method: "POST",
      body,
    });
  }

  async getBatchCosts() {
    return this.request<BatchCostOverview>("/api/v1/admin/eval/batch-costs");
  }

  async getBatchPage(pageId: string) {
    return this.request<BatchPageDetail>(
      `/api/v1/admin/eval/batch-pages/${encodeURIComponent(pageId)}`,
    );
  }

  async deleteBatchPage(pageId: string): Promise<void> {
    if (!this.hasSamsRuntimeHost) {
      throw new ApiError("SAMS 서버 주소가 설정되지 않아 페이지를 삭제할 수 없습니다.", 0, "SAMS_API_NOT_CONFIGURED");
    }
    await this.request<void>(`/api/v1/admin/eval/batch-pages/${encodeURIComponent(pageId)}`, { method: "DELETE" });
  }

  /** Rename any time; rewrite the set only while it is a draft. */
  async patchBatchPage(pageId: string, body: { name?: string; test_case_ids?: string[] }) {
    return this.request<BatchPageDetail>(
      `/api/v1/admin/eval/batch-pages/${encodeURIComponent(pageId)}`,
      { method: "PATCH", body },
    );
  }

  async lockBatchPage(pageId: string, testCaseIds: string[]) {
    return this.request<BatchPageDetail>(
      `/api/v1/admin/eval/batch-pages/${encodeURIComponent(pageId)}/lock`,
      { method: "POST", body: { test_case_ids: testCaseIds } },
    );
  }

  /** Records the run and starts it in the background; poll `getBatchRun`. */
  async startBatchRun(pageId: string, teSpec: TeSpec, rubric: BatchRubric = "v3") {
    return this.request<BatchRunView>(
      `/api/v1/admin/eval/batch-pages/${encodeURIComponent(pageId)}/runs`,
      { method: "POST", body: { te_spec: teSpec, rubric } },
    );
  }

  /** One run with every TC's state — the whole progress in one read (C7). */
  async getBatchRun(batchRunId: string) {
    return this.request<BatchRunView>(
      `/api/v1/admin/eval/batch-runs/${encodeURIComponent(batchRunId)}`,
    );
  }

  /** Runs on no page (MCP `run_batch`, scheduled runs), newest first — the page list never has them. */
  async listPagelessRuns({ cursor, limit = 30 }: { cursor?: string | null; limit?: number } = {}) {
    return this.request<PagelessRunListResponse>("/api/v1/admin/eval/batch-runs", {
      query: { page: "none", cursor: cursor || undefined, limit },
    });
  }

  /** One run as a one-column Matrix, in a page's detail shape. */
  async getBatchRunMatrix(batchRunId: string) {
    return this.request<MatrixPage>(
      `/api/v1/admin/eval/batch-runs/${encodeURIComponent(batchRunId)}/matrix`,
    );
  }

  /** The sidebar's badges and the backend's environment (BE-COUNTS). */
  async getEvalCounts() {
    return this.request<EvalCounts>("/api/v1/admin/eval/counts");
  }

  /** One TC under one run — replay, verdict, Judge detail and Reference. */
  async getBatchResult(batchRunId: string, testCaseId: string) {
    return this.request<BatchResultDetail>(
      `/api/v1/admin/eval/batch-runs/${encodeURIComponent(batchRunId)}/items/${encodeURIComponent(testCaseId)}`,
    );
  }

  // --- Reference Answer -----------------------------------------------------
  // Contract: base_agent_replay_lab/docs/sams-reference-api.md. Errors come as
  // `detail.error` {code, message}; `request` turns them into ApiError.

  /** The default model and what a request may override it with. */
  async getReferenceOptions() {
    return this.request<ReferenceOptions>("/api/v1/admin/eval/references/options");
  }

  /** Queues one regeneration of one TC; 202 `{job}`. 409 REFERENCE_JOB_ACTIVE names the running job. */
  async requestReference(testCaseId: string, body: ReferenceRequest = {}) {
    const { job } = await this.request<{ job: ReferenceJob }>(
      `/api/v1/admin/eval/test-cases/${encodeURIComponent(testCaseId)}/references`,
      { method: "POST", body },
    );
    return job;
  }

  /** Poll until succeeded/failed. 404 REFERENCE_JOB_NOT_FOUND after the 7-day retention. */
  async getReferenceJob(jobId: string) {
    const { job } = await this.request<{ job: ReferenceJob }>(
      `/api/v1/admin/eval/reference-jobs/${encodeURIComponent(jobId)}`,
    );
    return job;
  }

  /** Every version of one TC, newest first, with its open job, reviews and origin. */
  async listReferenceVersions(testCaseId: string) {
    return this.request<ReferenceVersionList>(
      `/api/v1/admin/eval/test-cases/${encodeURIComponent(testCaseId)}/references`,
    );
  }

  /**
   * Versions across TCs with their latest review, newest first. `scope:
   * "latest_passed"` keeps the most recently passed version of each TC. Prod
   * TCs are left out for an admin without prod access; `date_from`/`date_to`
   * are KST days on `created_at`.
   */
  async listReferences({ review_status, scope, source_environment, domain, date_from, date_to, q, include_inactive, cursor, limit = 30 }: { review_status?: ReferenceReviewStatus; scope?: "versions" | "latest_passed"; source_environment?: string; domain?: string; date_from?: string; date_to?: string; q?: string; include_inactive?: boolean; cursor?: string | null; limit?: number } = {}) {
    return this.request<ReferenceList>("/api/v1/admin/eval/references", {
      query: { review_status, scope, source_environment, domain, date_from, date_to, q: q || undefined, include_inactive: include_inactive ? "true" : undefined, cursor: cursor || undefined, limit },
    });
  }

  /**
   * Pass a version (scoring reads it from now), with or without a note, leave a note only (changes nothing
   * else), or reject it with a reason (its approval is cleared; `active_version` says what scoring reads then).
   */
  async reviewReference(snapshotId: string, body: ReferenceReviewRequest) {
    return this.request<ReferenceReviewResult>(
      `/api/v1/admin/eval/references/${encodeURIComponent(snapshotId)}/reviews`,
      { method: "POST", body },
    );
  }

  // --- Cause loop targets, ref 채점, TC loop progress (LOOPFLOW H/E) ---

  /** Active TCs whose Reference a person passed, by current score gap (gap DESC NULLS LAST, then id). */
  async listCauseLoopTargets({ cursor, limit = 30 }: { cursor?: string | null; limit?: number } = {}) {
    return this.request<CauseLoopTargetList>("/api/v1/admin/eval/cause-loop/targets", {
      query: { cursor: cursor || undefined, limit },
    });
  }

  /** ref 채점: each TC's human-passed Reference run judged once (v3), in the request; up to 3 TCs, one after
   * another (3 × the Judge's default 180 s, inside the proxy's 660 s), so the wait is 600 s. */
  async scoreReferences(testCaseIds: string[]) {
    return this.request<{ results: ReferenceScoreResult[] }>("/api/v1/admin/eval/cause-loop/reference-scores", {
      method: "POST",
      body: { test_case_ids: testCaseIds },
      timeoutMs: 600000,
    });
  }

  async getTcLoopProgress(testCaseId: string) {
    return this.request<{ test_case_id: string; progress: TcLoopProgress }>(
      `/api/v1/admin/eval/cause-loop/test-cases/${encodeURIComponent(testCaseId)}/progress`,
    );
  }

  /** 보류(deferred) or 다시 시작(unresolved), with a reason (one audit event; the state is computed). */
  async setTcLoopState(testCaseId: string, body: { state: "deferred" | "unresolved"; reason: string }) {
    return this.request<{ test_case_id: string; progress: TcLoopProgress }>(
      `/api/v1/admin/eval/cause-loop/test-cases/${encodeURIComponent(testCaseId)}/status`,
      { method: "PATCH", body },
    );
  }


  /** `source_environment` takes dev or prod only; omit it for both (BE has no `all`). */
  /** `date_from`/`date_to` are KST days (YYYY-MM-DD, both inclusive) on `created_at`. */
  async listCandidates({ status, pipeline_stage, position, test_case_id, source_environment, date_from, date_to, cursor, limit = 30 }: { status?: CandidateStatus; pipeline_stage?: "first" | "second"; position?: CandidatePosition; test_case_id?: string; source_environment?: CandidateSourceEnvironment; date_from?: string; date_to?: string; cursor?: string | null; limit?: number } = {}) {
    return this.request<{ items: Candidate[]; next_cursor: string | null }>("/api/v1/admin/eval/candidates", { query: { status, pipeline_stage, position, test_case_id, source_environment, date_from, date_to, cursor: cursor || undefined, limit } });
  }

  /** 1차 signals across runs, newest first; KST days on the detection time. */
  async listCandidateSignals({ source_environment, date_from, date_to, outcome, review_state, cursor, limit = 30 }: { source_environment?: CandidateSourceEnvironment; date_from?: string; date_to?: string; outcome?: CandidateSignalOutcome; review_state?: ReviewStageState; cursor?: string | null; limit?: number } = {}) {
    return this.request<CandidateSignalList>("/api/v1/admin/eval/candidates/signals", { query: { source_environment, date_from, date_to, outcome, review_state, cursor: cursor || undefined, limit } });
  }

  /** The 문제 식별 stage counts and TC rows; KST days on the day a signal was first found. */
  async getSignalStages({ source_environment, date_from, date_to }: { source_environment?: CandidateSourceEnvironment; date_from?: string; date_to?: string } = {}) {
    return this.request<SignalStages>("/api/v1/admin/eval/candidates/signals/stages", { query: { source_environment, date_from, date_to } });
  }

  /** Saved feedback-only 👎 of one environment (read-only; nothing is consumed or reclassified). */
  async listFeedbackOnly(environment: CandidateSourceEnvironment, { cursor, limit = 50 }: { cursor?: string | null; limit?: number } = {}) {
    return this.request<FeedbackOnlyList>(`/api/v1/admin/eval/feedback-inbox/${environment}/feedback-only`, { query: { cursor: cursor || undefined, limit } });
  }

  /** 1차 / retired 2차 / 확정 TC counts, computed with the same rules as `position`. */
  async getCandidateOverview({ source_environment }: { source_environment?: CandidateSourceEnvironment } = {}) {
    return this.request<CandidateOverview>("/api/v1/admin/eval/candidates/overview", { query: { source_environment } });
  }

  async getCandidate(id: string) {
    return this.request<{ item: Candidate }>(`/api/v1/admin/eval/candidates/${encodeURIComponent(id)}`);
  }

  /** Read-only record of the retired 2차 flow; its run and draft endpoints are no longer called. */
  async getGeneralizations(id: string) {
    return this.request<GeneralizationHistory>(`/api/v1/admin/eval/candidates/${encodeURIComponent(id)}/generalizations`);
  }

  async listDetectionBatches() {
    return this.request<{ items: JsonObject[]; failures: JsonObject[] }>("/api/v1/admin/eval/candidates/batches");
  }

  async getBatchDetections(batchId: string) {
    return this.request<BatchDetections>(`/api/v1/admin/eval/candidates/batches/${encodeURIComponent(batchId)}/detections`);
  }

  async getAnalysisContext(analysisId: string) {
    return this.request<AnalysisContext>(`/api/v1/admin/eval/candidates/analyses/${encodeURIComponent(analysisId)}/context`);
  }

  async manualPromote(analysisId: string, body: ManualPromotionBody) {
    return this.request<ManualPromotionResult>(
      `/api/v1/admin/eval/candidates/analyses/${encodeURIComponent(analysisId)}/manual-promote`, { method: "POST", body });
  }

  /** [다시 시도] of a candidate's error, run at once (a 1.1차 call may take minutes; the proxy allows 660s). */
  async retryCandidate(id: string) {
    return this.request<RetryResult>(
      `/api/v1/admin/eval/candidates/${encodeURIComponent(id)}/retry`, { method: "POST", timeoutMs: 660000 });
  }

  /** [다시 시도] of a 1차 signal's error row; `candidateId`: the card's candidate of that signal to retry. */
  async retrySignal(signalMessageId: string, source_environment: CandidateSourceEnvironment, candidateId?: string | null) {
    return this.request<RetryResult>(
      `/api/v1/admin/eval/candidates/signals/${encodeURIComponent(signalMessageId)}/retry`,
      { method: "POST", body: { source_environment, ...(candidateId ? { candidate_id: candidateId } : {}) }, timeoutMs: 660000 });
  }

  /** [정밀 탐지 다시] of any signal row: its 1.1차 called again now, not the stored verdict. */
  async rereviewSignal(signalMessageId: string, source_environment: CandidateSourceEnvironment) {
    return this.request<RereviewResult>(
      `/api/v1/admin/eval/candidates/signals/${encodeURIComponent(signalMessageId)}/rereview`,
      { method: "POST", body: { source_environment }, timeoutMs: 660000 });
  }

  /** [신호 탐지 다시]: that one message's 1차 scan called again now. */
  async rescanSignal(signalMessageId: string, source_environment: CandidateSourceEnvironment) {
    return this.request<RescanResult>(
      `/api/v1/admin/eval/candidates/signals/${encodeURIComponent(signalMessageId)}/rescan`,
      { method: "POST", body: { source_environment }, timeoutMs: 660000 });
  }

  async previewDetection(body: DetectionOptions) {
    return this.request<DetectionPreview>("/api/v1/admin/eval/candidates/detect/preview", { method: "POST", body });
  }

  async detectCandidates(body?: DetectionOptions) {
    return this.request<{ task_id: string; status: string; scope: DetectionOptions }>("/api/v1/admin/eval/candidates/detect", { method: "POST", body: body ?? {} });
  }

  async reviewCandidate(id: string, body: CandidateReview) {
    return this.request<{ item: Candidate }>(`/api/v1/admin/eval/candidates/${encodeURIComponent(id)}/review`, { method: "POST", body });
  }

  async splitCandidate(id: string, body: CandidateSplit) {
    return this.request<{ item: Candidate }>(`/api/v1/admin/eval/candidates/${encodeURIComponent(id)}/split`, { method: "POST", body });
  }

  async mergeCandidateSources(id: string, body: CandidateSourceMerge) {
    return this.request<{ item: Candidate }>(`/api/v1/admin/eval/candidates/${encodeURIComponent(id)}/merge-sources`, { method: "POST", body });
  }

  private patchAgentComponent(
    identifier: string,
    component: string,
    body: Record<string, unknown>,
  ) {
    return this.request<AgentState>(
      `/api/v1/admin/synapse-agents/${encodeURIComponent(identifier)}/${component}`,
      { method: "PATCH", body },
    );
  }

  private async request<T>(
    path: string,
    options: RequestOptions = {},
    baseUrl?: string,
  ): Promise<T> {
    if (this.samsLogin && !this.hasSamsRuntimeHost) {
      throw new ApiError(
        "SAMS API 주소(VITE_SAMS_API_BASE_URL)가 설정되지 않아 요청하지 않습니다.",
        0,
        "SAMS_API_NOT_CONFIGURED",
      );
    }
    if (this.evaluationRuntime && (options.method || "GET") === "GET") {
      if (path === "/api/v1/admin/proactive-messages/sessions") {
        path = "/api/v1/admin/eval/runtime/sessions";
      } else if (/^\/api\/v1\/admin\/(sessions\/|llm-calls(?:\/|$)|sub-agent-executions\/)/.test(path)) {
        path = path.replace("/api/v1/admin/", "/api/v1/admin/eval/runtime/");
      }
      if (path.startsWith("/api/v1/admin/eval/runtime/")) {
        if (!this.hasSamsRuntimeHost) {
          throw new ApiError("SAMS 평가 조회 주소가 설정되지 않았습니다.", 503, "SAMS_RUNTIME_NOT_CONFIGURED");
        }
        baseUrl = this.samsBaseUrl;
        options = { ...options, query: { ...options.query, environment: "sams" } };
      }
    }
    // The catalog is SAMS's own source of truth: a write sent to Base would
    // bypass it, so without a SAMS address nothing is sent.
    if (isModelCatalogPath(path) && !this.hasSamsRuntimeHost) {
      throw new ApiError("SAMS 서버 주소가 설정되지 않아 모델 카탈로그를 요청하지 않습니다.", 0,
        "SAMS_API_NOT_CONFIGURED");
    }
    const target = baseUrl ?? (isSamsManagementPath(path, options.method || "GET")
      ? this.samsBaseUrl || this.baseUrl : this.baseUrl);
    // With SAMS configured the bearer is a SAMS login token. Base rejects it,
    // and sending it there would leak it, so a path that still resolves to
    // Base fails here; the composer's Base writes are relayed by SAMS.
    if (!this.canCallBaseDirectly && trimSlash(target) === trimSlash(this.baseUrl)) {
      throw new ApiError(
        `SAMS 로그인으로는 Base API를 직접 호출하지 않습니다 (${path})`,
        0,
        "BASE_DIRECT_DISABLED",
      );
    }
    // The SAMS login token goes to the SAMS host only: another host (e.g. a
    // separate VITE_REPLAY_LAB_BASE_URL) is refused, not sent the token.
    if (this.samsLogin && originOf(target) !== originOf(this.samsBaseUrl!)) {
      throw new ApiError(
        `SAMS 로그인 토큰은 SAMS 주소로만 보냅니다 (${path} → ${originOf(target) ?? target})`,
        0,
        "SAMS_TOKEN_OTHER_HOST",
      );
    }
    const url = new URL(`${trimSlash(target)}${path}`);
    for (const [key, value] of Object.entries(options.query || {})) {
      if (value === undefined) continue;
      if (Array.isArray(value)) {
        for (const entry of value) {
          if (entry !== undefined) url.searchParams.append(key, String(entry));
        }
      } else {
        url.searchParams.set(key, String(value));
      }
    }
    const controller = new AbortController();
    const timeoutMs = options.timeoutMs || 60000;
    const method = options.method || "GET";
    // The timer's own abort says what happened and to which request (never the query or token).
    const timedOut = new ApiError(
      `서버 응답이 ${Math.round(timeoutMs / 1000)}초를 넘어 중단했습니다 (요청: ${method} ${url.pathname})`,
      0,
      "REQUEST_TIMEOUT",
    );
    const timeout = window.setTimeout(() => controller.abort(timedOut), timeoutMs);
    try {
      const response = await fetch(url, {
        method,
        headers: {
          Authorization: `Bearer ${this.bearerToken}`,
          ...(options.formData ? {} : { "Content-Type": "application/json" }),
        },
        body: options.formData
          ? options.formData
          : options.body === undefined
            ? undefined
            : JSON.stringify(options.body),
        signal: controller.signal,
      });
      const text = await response.text();
      const json = text ? JSON.parse(text) : null;
      if (!response.ok) {
        const detail = json?.error ? { error: json.error }
          : json?.detail || json?.message || text || response.statusText;
        // Admin routes wrap the failure as detail.error; anything else (plain
        // FastAPI strings included) falls through unchanged so callers that
        // match on the message text keep working.
        // The prompt-admin routes put {code, message} straight in detail.
        const structured =
          detail && typeof detail === "object" && "error" in detail
            ? (detail as { error?: { code?: unknown; message?: unknown } }).error
            : detail && typeof detail === "object" && "code" in detail && "message" in detail
              ? (detail as { code?: unknown; message?: unknown })
              : null;
        if (structured) {
          throw new ApiError(
            String(structured.message ?? response.statusText),
            response.status,
            String(structured.code ?? ""),
            structured as Record<string, unknown>,
          );
        }
        // FastAPI request validation (422) answers `detail: [{loc, msg, type}]`;
        // shown as "field: message" lines rather than raw JSON.
        if (Array.isArray(detail) && detail.every((d) => d && typeof d === "object" && "msg" in d)) {
          throw new ApiError(
            detail.map((d: { loc?: unknown; msg?: unknown }) => {
              const loc = Array.isArray(d.loc) ? d.loc.filter((part) => part !== "body").join(".") : "";
              return loc ? `${loc}: ${String(d.msg)}` : String(d.msg);
            }).join("; "),
            response.status,
            "REQUEST_VALIDATION",
          );
        }
        throw new ApiError(
          typeof detail === "string" ? detail : JSON.stringify(detail),
          response.status,
        );
      }
      return json as T;
    } catch (error) {
      if (controller.signal.reason === timedOut) throw timedOut;
      throw error;
    } finally {
      window.clearTimeout(timeout);
    }
  }

  private get normalizedBaseUrl() {
    return trimSlash(this.baseUrl);
  }
}

/** The scheme, host and port of an absolute URL; null when it is not one. */
function originOf(url: string): string | null {
  try {
    return new URL(trimSlash(url.trim())).origin;
  } catch {
    return null;
  }
}

function trimSlash(url: string) {
  return url.trim().replace(/\/+$/, "");
}

const SAMS_PREPARED_PROFILE_PREFIX = "/api/v1/sams/ai-profile/prepared/";
const SAMS_AGENT_METADATA =
  /^\/api\/v1\/admin\/synapse-agents(?:\/[^/]+(?:\/(?:catalog|prompt|skills))?)?$/;
const SAMS_AGENT_PROMOTION = /^\/api\/v1\/admin\/synapse-agents\/[^/]+\/promote-to-prod$/;
// Base's file/avatar admin handlers as the SAMS host mounts them
// (`sams_agent_assets.ASSET_ENDPOINTS`), each with its one method.
const SAMS_AGENT_ASSETS: ReadonlyArray<readonly [RegExp, readonly string[]]> = [
  [/^\/api\/v1\/admin\/synapse-agents\/[^/]+\/files$/, ["GET", "POST", "DELETE"]],
  [/^\/api\/v1\/admin\/synapse-agents\/[^/]+\/files\/content$/, ["GET"]],
  [/^\/api\/v1\/admin\/synapse-agents\/[^/]+\/avatar$/, ["PATCH"]],
  [/^\/api\/v1\/admin\/synapse-agents\/[^/]+\/avatar\/image$/, ["POST"]],
];

const SAMS_SOURCE_READS: readonly RegExp[] = [
  /^\/api\/v1\/admin\/sams\/admins\/me$/,
  /^\/api\/v1\/admin\/sams\/source\/users$/,
  /^\/api\/v1\/admin\/proactive-messages\/(?:users|sessions)$/,
  /^\/api\/v1\/admin\/sessions\/[^/]+\/messages(?:\/[^/]+\/llm-trace)?$/,
  /^\/api\/v1\/admin\/sub-agent-executions\/[^/]+\/rounds\/[^/]+\/llm-io$/,
];

function isModelCatalogPath(path: string) {
  return path === MODEL_CATALOG || path.startsWith(`${MODEL_CATALOG}/`);
}

function isSamsManagementPath(path: string, method: string): boolean {
  // The model catalog (models, versions, lookup, prices, requirements,
  // deployments, reconcile, policy) is authored and delivered by SAMS alone.
  if (isModelCatalogPath(path)) return true;
  const management = [
    "/api/v1/admin/eval/candidates",
    "/api/v1/admin/eval/test-cases",
    "/api/v1/admin/eval/batch-pages",
    "/api/v1/admin/eval/batch-runs",
    "/api/v1/admin/eval/replay-sources",
    "/api/v1/admin/eval/references",
    "/api/v1/admin/eval/reference-jobs",
    "/api/v1/admin/prompts",
    // SAMS admin management (list, events, grant, revoke, prod access) is
    // served by SAMS alone, for every method.
    "/api/v1/admin/sams/admins",
    "/api/v1/admin/sams/backend-errors",
    // Prompt deployment (status, preview, deploy) is SAMS's own policy.
    "/api/v1/admin/sams/prompt-deployments",
    // The composer's delegated sends, relayed to Base by SAMS.
    "/api/v1/admin/sams/proactive-messages",
  ];
  if (management.some((prefix) => path === prefix || path.startsWith(`${prefix}/`))) return true;
  if (["/api/v1/admin/eval/counts", "/api/v1/admin/eval/te-baseline"].includes(path)) return true;
  // Synapse-agent metadata (list, create, skill catalog, get/patch, and the
  // catalog/prompt/skills components) is SAMS authoring, and SAMS promotes
  // what it authored (promote-to-prod). Agent files and the avatar asset/upload
  // (the six SAMS_AGENT_ASSETS method/path pairs) go to the store SAMS maps for
  // its tenant/environment. All of these reach Base only when no SAMS address
  // is configured. The rendered avatar image is resolved by `absoluteUrl`: a
  // SAMS-served `/api/v1/sams/ai-profile/prepared/` path goes to SAMS when it
  // is configured, and Base's own ai-profile path stays on Base. Conversation
  // commands are Base product calls, not SAMS paths; the delegated sends are
  // SAMS's own relay (`/admin/sams/proactive-messages`, above).
  if (SAMS_AGENT_METADATA.test(path) || SAMS_AGENT_PROMOTION.test(path)) return true;
  const upper = method.toUpperCase();
  if (SAMS_AGENT_ASSETS.some(([pattern, methods]) => pattern.test(path) && methods.includes(upper))) return true;
  if (upper !== "GET") return false;
  // Read-only source consoles. SAMS serves them with Base's paths, params and
  // shapes through read-only dev/prod source readers, so a SAMS login never
  // needs Base for reading: the review/feed/log consoles, the user and session
  // search (conversation viewer and composer), session messages and llm-trace,
  // and the LLM call catalog and IO. Retained-evaluation reads were already
  // rewritten to /eval/runtime/ and pinned to SAMS before this check. The
  // model list is the registry SAMS itself runs with (read-only on SAMS).
  if (path === "/api/v1/admin/llm-models") return true;
  // The feedback-only 👎 records SAMS kept (its inbox pull stays a SAMS-side action the UI does not call).
  if (/^\/api\/v1\/admin\/eval\/feedback-inbox\/(?:dev|prod)\/feedback-only$/.test(path)) return true;
  const consoles = [
    "/api/v1/admin/negative-feedback",
    "/api/v1/admin/home-feed",
    "/api/v1/admin/proactive-messages/log",
    "/api/v1/admin/llm-calls",
  ];
  if (consoles.some((prefix) => path === prefix || path.startsWith(`${prefix}/`))) return true;
  return SAMS_SOURCE_READS.some((pattern) => pattern.test(path));
}

export function localized(value: Record<string, unknown> | undefined, fallback = "") {
  if (!value) return fallback;
  const ko = value.ko;
  if (typeof ko === "string" && ko) return ko;
  const en = value.en;
  if (typeof en === "string" && en) return en;
  const first = Object.values(value).find((item) => item !== undefined && item !== null);
  return first === undefined ? fallback : String(first);
}

export function agentIdentifier(agent: AgentState) {
  return agent.template.slug || agent.template.kind || agent.template.template_id;
}

/** Where a prompt's versions live: an editor target, or a Synapse agent's
 * prepared-session prompt (per kind and locale). */
export type PromptScope =
  | { target: string }
  | { preparedKind: string; locale: string };

function promptScopePath(scope: PromptScope) {
  return "target" in scope
    ? `/api/v1/admin/prompts/${encodeURIComponent(scope.target)}/versions`
    : `/api/v1/admin/prompts/prepared-sessions/${encodeURIComponent(scope.preparedKind)}/versions`;
}

function promptScopeQuery(scope: PromptScope) {
  return "target" in scope ? undefined : { locale: scope.locale };
}
